import { useDispatch } from "react-redux";
import { useCallback } from "react";
import { commodityTypeMasterApi } from "../API/getCommodityTypeMaster";
import { saveFilterCommodityTypeData, IFilterDropdown } from "../AppState/overagesFilterFormActions";

/**
 * Custom hook for managing commodity master data
 * 
 * This hook encapsulates the common commodity code loading logic used across multiple components.
 * It fetches commodity options from the commodityTypeMasterApi, sorts them, and saves them to Redux state.
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { loadCommodityOptions } = useCommodityMaster();
 *   const commodityOptions = useSelector((state: AppState) => state.commodityMasterData);
 * 
 *   useEffect(() => {
 *     if (!commodityOptions?.length) {
 *       loadCommodityOptions();
 *     }
 *   }, [loadCommodityOptions, commodityOptions?.length]);
 * 
 *   // Component JSX...
 * }
 * ```
 * 
 * @returns {Object} Hook methods
 * @returns {Function} loadCommodityOptions - Function to load and initialize commodity master data
 */
export const useCommodityMaster = () => {
  const dispatch = useDispatch();

  /**
   * Fetches commodity options, sorts them, and saves them to Redux state
   * 
   * This function:
   * 1. Calls the commodityTypeMasterApi API
   * 2. Trims the code values and sorts by numeric code value
   * 3. Dispatches the results to Redux using saveFilterCommodityTypeData
   * 4. Handles errors gracefully with console logging in development
   */
  const loadCommodityOptions = useCallback(() => {
    commodityTypeMasterApi()
      .then((results) => {
        const sortedData = results?.data
          ?.map((a: IFilterDropdown) => ({ ...a, code: a.code?.trim() }))
          .sort(
            (a: IFilterDropdown, b: IFilterDropdown) =>
              Number.parseInt(a.code!) - Number.parseInt(b.code!)
          );

        dispatch(saveFilterCommodityTypeData(sortedData));
      })
      .catch((error: any) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading commodity options:', error);
        }
      });
  }, [dispatch]);

  return {
    loadCommodityOptions
  };
};
